// @ts-nocheck -- legacy receiving contracts are migrated incrementally below.
import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";

import { classNames, formatCurrency } from "../../../../shared/utility";
import {
  makeGetApiCall,
  makePostApiCall,
} from "../../../../shared/makeApiCall";
import { resolveUnitOfMeasureLabel } from "../../../../shared/traceabilityFormatting";
import ExpandableDataTable from "../../../tables/ExpandableDataTable";
import { useRowDetailsState } from "../../../tables";
import EditProductSlideOver from "./EditProductSlideOver";
import ReceivingGroupInlineInput from "./ReceivingGroupInlineInput";

const primaryButtonClass =
  "inline-flex items-center justify-center rounded-xl border border-[#2563EB] bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:border-[#1D4ED8] hover:bg-[#1D4ED8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563EB]";

const neutralButtonClass =
  "inline-flex items-center justify-center rounded-xl border border-[#E2E8F0] bg-white px-4 py-2 text-sm font-semibold text-[#0F172A] shadow-sm transition hover:border-[#94A3B8] hover:bg-[#F6F8FB] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#64748B]";

const dangerButtonClass =
  "inline-flex items-center justify-center rounded-xl border border-[#DC2626] bg-[#DC2626] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:border-[#B91C1C] hover:bg-[#B91C1C] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#DC2626]";

function formatReceivedAmount(row) {
  const unit = resolveUnitOfMeasureLabel(
    row.unit_of_measure,
    row.unit_of_measure_label,
  );

  return unit ? `${row.received_amount} ${unit}` : `${row.received_amount}`;
}

const inlineNumberColumn = {
  width: "11rem",
  className: "overflow-visible",
  style: { minWidth: "11rem", width: "11rem" },
};

function ButtonSpinner() {
  return (
    <svg
      className="h-5 w-5 animate-spin text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function DeleteMultipleItemsModal({
  selectedCount,
  onConfirm,
  setEmptyModelOpen,
}) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    const deleteSuccess = await onConfirm(reason);
    setSubmitting(false);

    if (deleteSuccess) {
      setEmptyModelOpen({ childComponent: null, open: false });
    }
  };

  return (
    <div>
      <div className="text-center">
        <h3 className="text-base font-medium text-[#0F172A]">
          Delete selected items
        </h3>
        <p className="mt-2 text-sm text-[#64748B]">
          You are about to delete {selectedCount} receiving item
          {selectedCount !== 1 ? "s" : ""}.
        </p>
      </div>

      <div className="mt-5 flex items-center justify-center gap-3">
        <button
          type="button"
          className={neutralButtonClass}
          onClick={() =>
            setEmptyModelOpen({ childComponent: null, open: false })
          }
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className={dangerButtonClass}
          onClick={handleConfirm}
          disabled={submitting}
        >
          {submitting ? "Deleting..." : "Delete selected"}
        </button>
      </div>
    </div>
  );
}

const getLotItemsKey = (productRowKey, lotNumber) =>
  `${productRowKey}_${lotNumber}`;

export default function InvoiceItemsTable({
  invoiceItems,
  invoiceId,
  isEmployee,
  openReceivingItemDetails,
  deleteReceivingItem,
  deleteMultipleReceivingItems,
  setInvoiceItems,
  setEmptyModelOpen,
  setSlideOver,
  showErrorModal,
  onAddItem,
  addItemInProgress,
  showAddItem,
  itemsLocked = false,
  invoiceLocation,
  zoneOptions,
}) {
  // State for lazy-loaded lots (Level 2)
  const {
    loadingRows: lotsLoading,
    setRowLoading: setLotLoading,
    rowDetails: lotsData,
    setRowDetails: setLotsData,
    clearRowDetails: clearLotsRowDetails,
  } = useRowDetailsState();

  // State for lazy-loaded items (Level 3)
  const {
    loadingRows: itemsLoading,
    setRowLoading: setItemLoading,
    rowDetails: itemsData,
    setRowDetails: setItemsData,
    clearRowDetails: clearItemsRowDetails,
  } = useRowDetailsState();

  // Controlled expanded rows so we can collapse all after product edit
  const [expandedRows, setExpandedRows] = useState({});
  const [checkedItemRows, setCheckedItemRows] = useState({});
  const [deletingItemsKey, setDeletingItemsKey] = useState(null);
  const groupSaveQueuesRef = useRef({});
  const itemSaveQueuesRef = useRef({});
  const preserveSubTableCachesRef = useRef(false);

  const resetSubTableCaches = useCallback(() => {
    setExpandedRows({});
    setCheckedItemRows({});
    clearLotsRowDetails();
    clearItemsRowDetails();
  }, [clearLotsRowDetails, clearItemsRowDetails]);

  const isInitialInvoiceItems = useRef(true);

  useEffect(() => {
    if (isInitialInvoiceItems.current) {
      isInitialInvoiceItems.current = false;
      return;
    }
    if (preserveSubTableCachesRef.current) {
      preserveSubTableCachesRef.current = false;
      return;
    }
    resetSubTableCaches();
  }, [invoiceItems, resetSubTableCaches]);

  const selectedReceivingItemIds = useMemo(
    () =>
      Object.keys(checkedItemRows)
        .filter((receivingItemId) => checkedItemRows[receivingItemId])
        .map((receivingItemId) => parseInt(receivingItemId, 10)),
    [checkedItemRows],
  );

  const onConfirmDeleteSelected = useCallback(
    async (reason) => {
      if (itemsLocked) {
        return false;
      }
      if (!invoiceId || selectedReceivingItemIds.length === 0) {
        showErrorModal("Please select at least one item.");
        return false;
      }

      if (!deleteMultipleReceivingItems) {
        showErrorModal("Bulk delete is not available.");
        return false;
      }

      const callResult = await deleteMultipleReceivingItems(
        invoiceId,
        selectedReceivingItemIds,
      );

      resetSubTableCaches();
      if (callResult?.success) {
        return true;
      }

      return false;
    },
    [
      invoiceId,
      selectedReceivingItemIds,
      deleteMultipleReceivingItems,
      showErrorModal,
      resetSubTableCaches,
      itemsLocked,
    ],
  );

  const openDeleteSelectedModal = useCallback(() => {
    if (itemsLocked) {
      return;
    }
    if (selectedReceivingItemIds.length === 0) {
      showErrorModal("Please select at least one item.");
      return;
    }

    setEmptyModelOpen({
      childComponent: (
        <DeleteMultipleItemsModal
          selectedCount={selectedReceivingItemIds.length}
          onConfirm={onConfirmDeleteSelected}
          setEmptyModelOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  }, [
    selectedReceivingItemIds,
    showErrorModal,
    setEmptyModelOpen,
    onConfirmDeleteSelected,
    itemsLocked,
  ]);

  // After product save: collapse all rows and clear cached sub-items so they reload on next expand
  const onProductSaveSuccess = useCallback(() => {
    resetSubTableCaches();
  }, [resetSubTableCaches]);

  const handleDeleteReceivingItem = useCallback(
    async (receivingItemId, productRowKey, assemblyItemId, lotNumber) => {
      if (itemsLocked) {
        return;
      }
      const itemsKey = getLotItemsKey(productRowKey, lotNumber);

      setDeletingItemsKey(itemsKey);
      setItemLoading(itemsKey, true);
      try {
        const callResult = await deleteReceivingItem(receivingItemId);
        if (!callResult?.success) return;

        // Invalidate cached items so re-expand refetches from API
        clearItemsRowDetails(itemsKey);

        setCheckedItemRows((prev) => {
          if (!prev[receivingItemId]) return prev;
          const next = { ...prev };
          delete next[receivingItemId];
          return next;
        });

        const lots = callResult.data?.items_by_lot_and_item;
        if (lots) {
          setLotsData(productRowKey, lots);
        }
      } finally {
        setItemLoading(itemsKey, false);
        setDeletingItemsKey(null);
      }
    },
    [
      deleteReceivingItem,
      clearItemsRowDetails,
      setLotsData,
      setItemLoading,
      itemsLocked,
    ],
  );

  const getProductRowKey = useCallback(
    (product) => product.unique_id ?? String(product.assembly_item_id),
    [],
  );

  const saveProductInline = useCallback(
    (row, field, value) => {
      const key = getProductRowKey(row);
      const operation = async () => {
        const payload = {
          group_zone_id: Number(row.zone?.value ?? 0),
          [field]: value,
        };
        const callResult = await makePostApiCall(
          `updateReceivingAssemblyItemGroup/${invoiceId}/${row.assembly_item_id}`,
          payload,
        );

        if (!callResult.success) {
          showErrorModal(
            callResult.data?.message || "Could not update receiving totals.",
          );
          return false;
        }

        setInvoiceItems(callResult.data?.receiving_items ?? []);
        return true;
      };

      const previous = groupSaveQueuesRef.current[key] ?? Promise.resolve();
      const task = previous.catch(() => false).then(operation);
      groupSaveQueuesRef.current[key] = task;
      task.finally(() => {
        if (groupSaveQueuesRef.current[key] === task) {
          delete groupSaveQueuesRef.current[key];
        }
      });
      return task;
    },
    [getProductRowKey, invoiceId, setInvoiceItems, showErrorModal],
  );

  const saveItemInline = useCallback(
    (row, productRowKey, assemblyItemId, lotNumber, field, value) => {
      const itemId = row.receiving_item_id;
      const itemsKey = getLotItemsKey(productRowKey, lotNumber);
      const operation = async () => {
        const callResult = await makePostApiCall(
          `updateReceivingAssemblyItemFinancials/${itemId}`,
          { [field]: value },
        );

        if (!callResult.success) {
          showErrorModal(
            callResult.data?.message || "Could not update the receiving item.",
          );
          return false;
        }

        const updatedItem = callResult.data?.receiving_item;
        if (updatedItem) {
          setItemsData(
            itemsKey,
            (itemsData[itemsKey] ?? []).map((item) =>
              item.receiving_item_id === itemId
                ? { ...item, ...updatedItem }
                : item,
            ),
          );
        }

        const zoneId = Number(
          invoiceItems.find(
            (product) => getProductRowKey(product) === productRowKey,
          )?.zone?.value ?? 0,
        );
        const [productsResult, lotsResult] = await Promise.all([
          makeGetApiCall(`invoiceProducts/${invoiceId}`),
          makeGetApiCall(
            `invoiceLots/${invoiceId}/${assemblyItemId}/${zoneId}`,
          ),
        ]);

        if (productsResult.success) {
          preserveSubTableCachesRef.current = true;
          setInvoiceItems(productsResult.data?.receiving_items ?? []);
        }
        if (lotsResult.success) {
          setLotsData(
            productRowKey,
            lotsResult.data?.items_by_lot_and_item ?? [],
          );
        }

        return true;
      };

      const previous = itemSaveQueuesRef.current[itemId] ?? Promise.resolve();
      const task = previous.catch(() => false).then(operation);
      itemSaveQueuesRef.current[itemId] = task;
      task.finally(() => {
        if (itemSaveQueuesRef.current[itemId] === task) {
          delete itemSaveQueuesRef.current[itemId];
        }
      });
      return task;
    },
    [
      getProductRowKey,
      invoiceId,
      invoiceItems,
      itemsData,
      setInvoiceItems,
      setItemsData,
      setLotsData,
      showErrorModal,
    ],
  );

  // Column definitions for Level 1 (Products)
  const productColumns = useMemo(() => {
    const columns = [
      { header: "Product Name", field: "product_name" },
      { header: "Zone", field: "zone.label", align: "left" },
      { header: "Total Cartons", field: "total_cartons", align: "right" },

      {
        header: "Total Unit",
        body: (row) =>
          isEmployee ? (
            formatReceivedAmount(row)
          ) : (
            <ReceivingGroupInlineInput
              ariaLabel={`Total units for ${row.product_name}`}
              value={row.received_amount}
              suffix={resolveUnitOfMeasureLabel(
                row.unit_of_measure,
                row.unit_of_measure_label,
              )}
              onCommit={(value) =>
                saveProductInline(row, "new_received_amount", value)
              }
            />
          ),
        align: "right",
        ...inlineNumberColumn,
      },
    ];

    if (!isEmployee) {
      columns.push({
        header: "Unit Cost",
        field: "unit_cost",
        body: (row) => (
          <ReceivingGroupInlineInput
            ariaLabel={`Unit cost for ${row.product_name}`}
            value={row.unit_cost}
            currency
            onCommit={(value) => saveProductInline(row, "new_unit_cost", value)}
          />
        ),
        align: "right",
        ...inlineNumberColumn,
      });
      columns.push({
        header: "Total Cost",
        field: "total_cost",
        body: (row) => (
          <ReceivingGroupInlineInput
            ariaLabel={`Total cost for ${row.product_name}`}
            value={row.total_cost}
            currency
            onCommit={(value) =>
              saveProductInline(row, "new_total_cost", value)
            }
          />
        ),
        align: "right",
        ...inlineNumberColumn,
      });
      columns.push({
        header: "",
        body: (row) => (
          <button
            type="button"
            onClick={() =>
              setSlideOver({
                open: true,
                childComponent: (
                  <EditProductSlideOver
                    product={row}
                    invoiceId={invoiceId}
                    invoiceLocation={invoiceLocation}
                    zoneOptions={zoneOptions}
                    setSlideOver={setSlideOver}
                    setInvoiceItems={setInvoiceItems}
                    setEmptyModelOpen={setEmptyModelOpen}
                    showErrorModal={showErrorModal}
                    onSaveSuccess={onProductSaveSuccess}
                  />
                ),
              })
            }
            className="rounded-lg border border-[#2563EB] bg-[#2563EB] px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:border-[#1D4ED8] hover:bg-[#1D4ED8]"
          >
            Edit
          </button>
        ),
        width: "80px",
      });
    }

    return columns;
  }, [
    isEmployee,
    invoiceId,
    invoiceLocation,
    zoneOptions,
    setSlideOver,
    setInvoiceItems,
    setEmptyModelOpen,
    showErrorModal,
    onProductSaveSuccess,
    saveProductInline,
  ]);

  // Column definitions for Level 2 (Lots)
  const lotColumns = useMemo(() => {
    const columns = [
      { header: "Lot Number", field: "lot_number" },
      { header: "Total Cartons", field: "carton_count", align: "right" },
      {
        header: "Total Unit",
        body: formatReceivedAmount,
        align: "right",
      },
    ];

    if (!isEmployee) {
      columns.push({
        header: "Total Cost",
        body: (row) => formatCurrency(row.total_cost),
        align: "right",
      });
    }

    return columns;
  }, [isEmployee]);

  const getItemColumns = useCallback(
    (items, productRowKey, assemblyItemId, lotNumber) => {
      const allItemsSelected =
        items.length > 0 &&
        items.every((row) => checkedItemRows[row.receiving_item_id]);
      const someItemsSelected =
        items.some((row) => checkedItemRows[row.receiving_item_id]) &&
        !allItemsSelected;

      const toggleSelectAll = () => {
        setCheckedItemRows((prev) => {
          const next = { ...prev };

          if (allItemsSelected) {
            items.forEach((row) => {
              delete next[row.receiving_item_id];
            });
            return next;
          }

          items.forEach((row) => {
            next[row.receiving_item_id] = true;
          });

          return next;
        });
      };

      const columns = [
        {
          header: (
            <div className="flex justify-center">
              <input
                type="checkbox"
                checked={allItemsSelected}
                ref={(element) => {
                  if (element) {
                    element.indeterminate = someItemsSelected;
                  }
                }}
                onChange={toggleSelectAll}
                className="h-4 w-4 rounded border-[#E2E8F0] text-[#2563EB] focus:ring-[#2563EB]"
                aria-label="Select all items in this lot"
              />
            </div>
          ),
          width: "48px",
          body: (row) => (
            <div className="flex justify-center">
              <input
                type="checkbox"
                checked={checkedItemRows[row.receiving_item_id]}
                onChange={() =>
                  setCheckedItemRows((prev) => ({
                    ...prev,
                    [row.receiving_item_id]: !prev[row.receiving_item_id],
                  }))
                }
                className="h-4 w-4 rounded border-[#E2E8F0] text-[#2563EB] focus:ring-[#2563EB]"
                aria-label={`Select item ${row.receiving_item_id}`}
              />
            </div>
          ),
        },
        {
          header: "Product Name",
          field: "product_name",
          headerStyle: { textAlign: "left" },
        },
        {
          header: "Barcode",
          field: "barcode",
          headerStyle: { textAlign: "left" },
        },
        {
          header: "Unit",
          body: (row) =>
            isEmployee ? (
              formatReceivedAmount(row)
            ) : (
              <ReceivingGroupInlineInput
                ariaLabel={`Received quantity for ${row.product_name}`}
                value={row.received_amount}
                suffix={resolveUnitOfMeasureLabel(
                  row.unit_of_measure,
                  row.unit_of_measure_label,
                )}
                onCommit={(value) =>
                  saveItemInline(
                    row,
                    productRowKey,
                    assemblyItemId,
                    lotNumber,
                    "new_received_amount",
                    value,
                  )
                }
              />
            ),
          align: "right",
          ...inlineNumberColumn,
        },
      ];

      if (!isEmployee) {
        columns.push({
          header: "Unit Cost",
          body: (row) => (
            <ReceivingGroupInlineInput
              ariaLabel={`Unit cost for ${row.product_name}`}
              value={row.unit_cost}
              currency
              onCommit={(value) =>
                saveItemInline(
                  row,
                  productRowKey,
                  assemblyItemId,
                  lotNumber,
                  "new_unit_cost",
                  value,
                )
              }
            />
          ),
          align: "right",
          ...inlineNumberColumn,
        });
        columns.push({
          header: "Total Cost",
          body: (row) => (
            <ReceivingGroupInlineInput
              ariaLabel={`Total cost for ${row.product_name}`}
              value={row.total_cost}
              currency
              onCommit={(value) =>
                saveItemInline(
                  row,
                  productRowKey,
                  assemblyItemId,
                  lotNumber,
                  "new_total_cost",
                  value,
                )
              }
            />
          ),
          align: "right",
          ...inlineNumberColumn,
        });
      }

      columns.push({
        header: "",
        align: "right",
        body: (row) => (
          <div className="text-right flex flex-col md:flex-row md:items-center gap-3 md:gap-2 md:justify-end">
            <button
              type="button"
              onClick={() => openReceivingItemDetails(row.receiving_item_id)}
              className="rounded-lg border border-[#2563EB] bg-[#2563EB] px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:border-[#1D4ED8] hover:bg-[#1D4ED8]"
            >
              View
            </button>
            <button
              type="button"
              onClick={() =>
                handleDeleteReceivingItem(
                  row.receiving_item_id,
                  productRowKey,
                  assemblyItemId,
                  lotNumber,
                )
              }
              disabled={itemsLocked}
              className={classNames(
                itemsLocked
                  ? "cursor-not-allowed border-[#E2E8F0] bg-[#F6F8FB] text-[#64748B]"
                  : "border-[#DC2626] bg-[#DC2626] text-white hover:border-[#B91C1C] hover:bg-[#B91C1C]",
                "rounded-lg border px-3 py-1.5 text-sm font-semibold shadow-sm transition",
              )}
            >
              Delete
            </button>
          </div>
        ),
      });

      return columns;
    },
    [
      checkedItemRows,
      handleDeleteReceivingItem,
      isEmployee,
      itemsLocked,
      openReceivingItemDetails,
      saveItemInline,
    ],
  );

  // Handle product row expand - fetch lots
  const handleProductExpand = useCallback(
    async (product) => {
      const key = getProductRowKey(product);
      if (lotsData[key]) return; // Already loaded

      setLotLoading(key, true);
      try {
        const zoneId = product.zone?.value ?? 0;
        const callResult = await makeGetApiCall(
          `invoiceLots/${invoiceId}/${product.assembly_item_id}/${zoneId}`,
        );
        if (callResult.success) {
          setLotsData(key, callResult.data?.items_by_lot_and_item ?? []);
        } else {
          console.error("Failed to fetch lots:", callResult.data?.message);
          setLotsData(key, []);
        }
      } catch (error) {
        console.error("Failed to fetch lots:", error);
        setLotsData(key, []);
      }
      setLotLoading(key, false);
    },
    [invoiceId, lotsData, setLotLoading, setLotsData, getProductRowKey],
  );

  // Handle lot row expand - fetch items
  const handleLotExpand = useCallback(
    async (lot, productRowKey, assemblyItemId) => {
      const key = getLotItemsKey(productRowKey, lot.lot_number);
      if (itemsData[key]) return; // Already loaded

      setItemLoading(key, true);
      try {
        const callResult = await makeGetApiCall(
          `invoiceLotItems/${invoiceId}/${assemblyItemId}/${encodeURIComponent(lot.lot_number)}`,
        );
        if (callResult.success) {
          setItemsData(key, callResult.data.items || []);
        } else {
          console.error("Failed to fetch items:", callResult.data?.message);
          setItemsData(key, []);
        }
      } catch (error) {
        console.error("Failed to fetch items:", error);
        setItemsData(key, []);
      }
      setItemLoading(key, false);
    },
    [invoiceId, itemsData, setItemLoading, setItemsData],
  );

  // Render Level 3: Items table
  const renderItemsTable = useCallback(
    (lot, productRowKey, assemblyItemId) => {
      const key = getLotItemsKey(productRowKey, lot.lot_number);
      const items = itemsData[key];

      if (itemsLoading[key]) {
        const isDeleting = deletingItemsKey === key;
        return (
          <div className="ml-3 p-4 text-center">
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: "1.5rem" }}
            />
            <p className="mt-2 text-[#64748B]">
              {isDeleting ? "Deleting item..." : "Loading items..."}
            </p>
          </div>
        );
      }

      if (!items || items.length === 0) {
        return (
          <div className="ml-3 p-4 text-center text-[#64748B]">
            No items available
          </div>
        );
      }

      const selectionRenderKey = items
        .map(
          (row) =>
            `${row.receiving_item_id}:${checkedItemRows[row.receiving_item_id] ? 1 : 0}`,
        )
        .join("|");

      return (
        <div className="ml-3">
          <ExpandableDataTable
            key={`${key}_${selectionRenderKey}`}
            data={items}
            dataKey="receiving_item_id"
            columns={getItemColumns(
              items,
              productRowKey,
              assemblyItemId,
              lot.lot_number,
            )}
            tableClassName="min-w-full border-separate border-spacing-0 bg-white text-sm"
            cellClassName="border-b border-[#E2E8F0]/70 bg-[#F6F8FB] px-2 py-2 text-sm text-[#0F172A]"
            wrapperClassName="receiving-invoice-items-table"
          />
        </div>
      );
    },
    [
      itemsLoading,
      itemsData,
      checkedItemRows,
      deletingItemsKey,
      getItemColumns,
    ],
  );

  // Render Level 2: Lots table
  const renderLotsTable = useCallback(
    (product) => {
      const key = getProductRowKey(product);

      if (lotsLoading[key]) {
        return (
          <div className="ml-2 p-4 text-center">
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: "1.5rem" }}
            />
            <p className="mt-2 text-[#64748B]">Loading lots...</p>
          </div>
        );
      }

      const lots = lotsData[key];
      if (!lots || lots.length === 0) {
        return (
          <div className="ml-2 p-4 text-center text-[#64748B]">
            No lots available
          </div>
        );
      }

      return (
        <div className="ml-2 p-1">
          <ExpandableDataTable
            data={lots}
            dataKey="unique_id"
            columns={lotColumns}
            expandable
            onRowExpand={(lot) =>
              handleLotExpand(lot, key, product.assembly_item_id)
            }
            renderExpandedRow={(lot) =>
              renderItemsTable(lot, key, product.assembly_item_id)
            }
            tableClassName="min-w-full border-separate border-spacing-0 bg-white text-sm"
            cellClassName="border-b border-[#E2E8F0]/70 px-2 py-2 text-sm text-[#0F172A]"
            wrapperClassName="receiving-invoice-lots-table"
          />
        </div>
      );
    },
    [
      lotsLoading,
      lotsData,
      lotColumns,
      handleLotExpand,
      renderItemsTable,
      getProductRowKey,
    ],
  );

  const items = invoiceItems ?? [];
  const hasInvoiceItems = items.length > 0;

  // Calculate totals for summary footer (only shown when there are rows)
  const totalProducts = items.length;
  const totalCartons = items.reduce(
    (sum, p) => sum + parseFloat(p.total_cartons || 0),
    0,
  );
  const totalCost = items
    .reduce((sum, p) => sum + parseFloat(p.total_cost || 0), 0)
    .toFixed(2);

  const summaryFooter = hasInvoiceItems ? (
    <div className="border-t border-[#E2E8F0] bg-[#F6F8FB] p-4">
      <div className="flex justify-between text-sm font-semibold text-[#0F172A]">
        <span>
          {totalProducts} product{totalProducts !== 1 ? "s" : ""}
        </span>
        <span>
          {totalCartons} carton{totalCartons !== 1 ? "s" : ""}
        </span>
        {!isEmployee && <span>${totalCost}</span>}
      </div>
    </div>
  ) : null;

  return (
    <div id="barcode-scanned-items-parent">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h4 className="text-base font-semibold text-[#0F172A]">
          Received items
        </h4>
        <div className="flex flex-wrap items-center gap-2">
          {showAddItem ? (
            <button
              type="button"
              onClick={onAddItem}
              disabled={addItemInProgress || itemsLocked}
              className={primaryButtonClass}
            >
              {addItemInProgress ? <ButtonSpinner /> : "Add item"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={openDeleteSelectedModal}
            disabled={itemsLocked || selectedReceivingItemIds.length === 0}
            className={classNames(
              itemsLocked || selectedReceivingItemIds.length === 0
                ? "cursor-not-allowed border-[#E2E8F0] bg-[#F6F8FB] text-[#64748B]"
                : "border-[#DC2626] bg-[#DC2626] text-white hover:border-[#B91C1C] hover:bg-[#B91C1C]",
              "rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition",
            )}
          >
            Delete selected ({selectedReceivingItemIds.length})
          </button>
        </div>
      </div>
      <div
        id="product-table"
        className="overflow-x-auto rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
      >
        <ExpandableDataTable
          data={items}
          dataKey="unique_id"
          columns={productColumns}
          expandable
          expandedRows={expandedRows}
          onExpandedRowsChange={setExpandedRows}
          onRowExpand={handleProductExpand}
          renderExpandedRow={renderLotsTable}
          tableClassName="min-w-full border-separate border-spacing-0 bg-white text-sm"
          cellClassName="border-b border-[#E2E8F0]/70 bg-white p-2 text-sm text-[#0F172A]"
          wrapperClassName="receiving-invoice-products-table"
          emptyMessage="No items received yet"
          footer={summaryFooter}
        />
      </div>
    </div>
  );
}
